import { flexRender, type RowData } from "@tanstack/react-table";
import { useMemo } from "react";
import { useLocation } from "react-router";

import { useMetricHelp } from "@/components/Analytics/useMetricHelp";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ChartLegend } from "@/components/Charts/ChartLegend";
import { HistogramChart } from "@/components/Charts/HistogramChart";
import { dataColors } from "@/components/Charts/theme";
import { FilterFunnel01Icon } from "@/components/Icons/FilterFunnel01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { addPortfolioProjectFilterQueryParams } from "@/components/ProjectFilter/portfolioProjectFilterQueryParams";
import { Stat } from "@/components/Stat/Stat";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { NumericBadge } from "@/design-system/NumericBadge";
import { SectionCard } from "@/design-system/SectionCard";
import { Select } from "@/design-system/Select";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import type { DashboardTable } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { formatSignedChange } from "@/helpers/util";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSlug } from "@/hooks/useSlug";
import { useAnalyticsScope } from "@/modules/analytics/AnalyticsScopeContext";
import { AnalyticsCard } from "@/modules/analytics/components/AnalyticsCard";
import { AnalyticsPageContent } from "@/modules/analytics/components/AnalyticsPageContent";
import { AnalyticsSection } from "@/modules/analytics/components/AnalyticsSection";
import { BarChart } from "@/modules/analytics/components/BarChart";
import { LineChart } from "@/modules/analytics/components/LineChart";
import { ANALYTICS_AUDIENCE_SCOPE_COLORS } from "@/modules/analytics/constants";
import { useConfig } from "@/providers/config/useConfig";
import type {
  CommunityFeedAnalyticsContentPurpose,
  CommunityFeedAnalyticsContentType,
  CommunityFeedAnalyticsContributorFrequencyBucket,
  CommunityFeedAnalyticsGroup,
  CommunityFeedAnalyticsGroupType,
  CommunityFeedAnalyticsInteractionBucket,
  CommunityFeedAnalyticsResponse,
  CommunityFeedAnalyticsResponseBucket,
} from "@/queries/analytics/community-feed-reporting-types";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

import type { CommunityFeedAnalyticsDateRangeOption } from "./CommunityFeedAnalyticsPage";
import { PollAnalyticsSection } from "./PollAnalyticsSection";
import { SentimentSummaryCard } from "./SentimentSummaryCard";

interface Props {
  mode?: "focused" | "all";
  data: CommunityFeedAnalyticsResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  selectedDateRange: CommunityFeedAnalyticsDateRangeOption;
  dateRangeOptions: NonEmptyArray<CommunityFeedAnalyticsDateRangeOption>;
  onDateRangeChange: (option: CommunityFeedAnalyticsDateRangeOption) => void;
  activeFilterCount: number;
  onOpenFilters: () => void;
  groupTable: DashboardTable<CommunityFeedAnalyticsGroup>;
  contentType: CommunityFeedAnalyticsContentType;
}

export function CommunityFeedAnalyticsContent({
  mode = "all",
  data,
  isLoading,
  isFetching,
  selectedDateRange,
  dateRangeOptions,
  onDateRangeChange,
  activeFilterCount,
  onOpenFilters,
  groupTable,
  contentType,
}: Props): React.ReactNode {
  const { t } = useTranslation();
  const analyticsScope = useAnalyticsScope();
  const location = useLocation();
  const slug = useSlug({ optional: true });
  const projectId = useProjectId();
  const portfolioRoute = (path: string) => addPortfolioProjectFilterQueryParams(path, location.search);
  const combinedFilterCount =
    activeFilterCount + (analyticsScope.type === "portfolio" ? analyticsScope.projectFilter.activeCount : 0);

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={
        mode === "focused" ? t("page.analytics-community-feed.focus.title") : t("page.analytics-community-feed.title")
      }
      title={
        mode === "focused" ? t("page.analytics-community-feed.focus.title") : t("page.analytics-community-feed.title")
      }
      breadcrumbs={
        <Breadcrumbs
          pages={[
            analyticsScope.type === "portfolio"
              ? { name: t("page.portfolio.title"), to: portfolioRoute(routes.portfolio.overview()) }
              : { name: t("page.analytics-story.title"), to: routes.analytics.overview({ slug: slug! }) },
            { name: t("page.analytics-community-feed.breadcrumb") },
          ]}
        />
      }
      actions={
        <AskNobiButton
          defaultFilters={{ projectIds: analyticsScope.type === "portfolio" ? analyticsScope.projectIds : [projectId] }}
          context={{ scope: analyticsScope.type, entity: "analytics", page: "detail" }}
          suggestions={[
            t("component.ask-nobi-chat-drawer.analytics.suggestion.1"),
            t("component.ask-nobi-chat-drawer.analytics.suggestion.2"),
          ]}
        />
      }
      header={
        <Inline gap="md" align="center">
          <Inline gap="sm" align="center" wrap={false}>
            <span className="text-body-emphasis">{t("page.analytics-details.time-range.label")}</span>
            <Select
              items={dateRangeOptions}
              selected={selectedDateRange}
              onChange={onDateRangeChange}
              renderOption={(option) => option.name}
              keySelector={(option) => option.value}
            />
          </Inline>
          <Button variant="soft" icon={<FilterFunnel01Icon />} onClick={onOpenFilters}>
            <Inline as="span" gap="sm" align="center" wrap={false}>
              {t("common.action.filter")}
              {combinedFilterCount > 0 && <NumericBadge count={combinedFilterCount} variant="filled" />}
            </Inline>
          </Button>
          {isFetching && !isLoading && <Spinner size="sm" />}
        </Inline>
      }
    >
      <AnalyticsPageContent>
        {mode === "focused" && (
          <p className="max-w-measure-wide text-body text-text-secondary">
            {t("page.analytics-community-feed.focus.description")}
          </p>
        )}
        {isLoading || !data ? (
          <section className="flex flex-1 items-center justify-center py-space-xl" aria-busy="true">
            <Spinner size="xl" />
          </section>
        ) : (
          <Content
            mode={mode}
            data={data}
            groupTable={groupTable}
            contentType={contentType}
            selectedDateRange={selectedDateRange}
            showAudienceParticipation={analyticsScope.type === "project"}
          />
        )}
      </AnalyticsPageContent>
    </DocumentPage>
  );
}

function Content({
  mode,
  data,
  groupTable,
  contentType,
  selectedDateRange,
  showAudienceParticipation,
}: Pick<Props, "mode" | "groupTable" | "contentType" | "selectedDateRange"> & {
  data: CommunityFeedAnalyticsResponse;
  showAudienceParticipation: boolean;
}): React.ReactNode {
  const { i18n, t } = useTranslation();
  const help = useMetricHelp();
  const isSentimentAvailable = useConfig("isCommunityFeedSentimentAvailable");
  const { summary } = data;
  const residentPostsData = useMemo(
    () =>
      data.monthly.map((point) => ({
        x: point.x,
        y: point.residentPosts,
        isPending: point.isCurrentMonth,
      })),
    [data.monthly],
  );
  const projectTeamPostsData = useMemo(
    () =>
      data.monthly.map((point) => ({
        x: point.x,
        y: point.projectTeamPosts,
        isPending: point.isCurrentMonth,
      })),
    [data.monthly],
  );
  const contributorData = useMemo(
    () => [
      {
        label: t("page.analytics-community-feed.participation.contributors"),
        color: dataColors.green,
        dataPoints: data.monthly.map((point) => ({
          x: point.x,
          y: point.residentContributors,
          isPending: point.isCurrentMonth,
        })),
      },
    ],
    [data.monthly, t],
  );
  const contributorReachData = useMemo(
    () => [
      {
        label: t("page.analytics-community-feed.ownership.contributor-reach"),
        color: dataColors.mint,
        dataPoints: data.monthly.map((point) => ({
          x: point.x,
          y: point.contributorReachPercentage,
          isPending: point.isCurrentMonth,
        })),
      },
    ],
    [data.monthly, t],
  );
  const contributorFrequencyData = useMemo(
    () =>
      data.contributorFrequencyDistribution.map((point) => ({
        category: point.x as CommunityFeedAnalyticsContributorFrequencyBucket,
        label: t(
          `page.analytics-community-feed.contributor-frequency.${point.x as CommunityFeedAnalyticsContributorFrequencyBucket}`,
        ),
        value: point.y,
      })),
    [data.contributorFrequencyDistribution, t],
  );
  const contentPurposeData = useMemo(
    () => [
      {
        label: t("page.analytics-community-feed.content.information"),
        color: dataColors.primary,
        dataPoints: data.monthly.map((point) => ({
          x: point.x,
          y: point.informationPosts,
          isPending: point.isCurrentMonth,
        })),
      },
      {
        label: t("page.analytics-community-feed.content.help-requests"),
        color: dataColors.green,
        dataPoints: data.monthly.map((point) => ({
          x: point.x,
          y: point.helpRequests,
          isPending: point.isCurrentMonth,
        })),
      },
      {
        label: t("page.analytics-community-feed.content.marketplace"),
        color: dataColors.orange,
        dataPoints: data.monthly.map((point) => ({
          x: point.x,
          y: point.marketplacePosts,
          isPending: point.isCurrentMonth,
        })),
      },
    ],
    [data.monthly, t],
  );
  const audienceTypeData = useMemo(
    () =>
      data.audienceTypeDistribution.map((point) => ({
        category: point.x as CommunityFeedAnalyticsGroupType,
        label: t(`page.analytics-community-feed.group-type.${point.x as CommunityFeedAnalyticsGroupType}`),
        value: point.y,
      })),
    [data.audienceTypeDistribution, t],
  );
  const interactionData = useMemo(
    () => [
      {
        label: t("page.analytics-community-feed.participation.comments"),
        color: dataColors.primary,
        dataPoints: data.monthly.map((point) => ({
          x: point.x,
          y: point.comments,
          isPending: point.isCurrentMonth,
        })),
      },
      {
        label: t("page.analytics-community-feed.participation.reactions"),
        color: dataColors.orange,
        dataPoints: data.monthly.map((point) => ({
          x: point.x,
          y: point.reactions,
          isPending: point.isCurrentMonth,
        })),
      },
    ],
    [data.monthly, t],
  );
  const interactionsPerPostData = useMemo(
    () => [
      {
        label: t("page.analytics-community-feed.participation.interactions-per-post"),
        color: dataColors.purple,
        dataPoints: data.monthly.map((point) => ({
          x: point.x,
          y: point.interactionsPerPost,
          isPending: point.isCurrentMonth,
        })),
      },
    ],
    [data.monthly, t],
  );
  const interactionDistributionData = useMemo(
    () =>
      data.interactionDistribution.map((point) => ({
        category: point.x as CommunityFeedAnalyticsInteractionBucket,
        label: t(
          `page.analytics-community-feed.interaction-level.${point.x as CommunityFeedAnalyticsInteractionBucket}`,
        ),
        value: point.y,
      })),
    [data.interactionDistribution, t],
  );
  const engagementByContentPurposeData = useMemo(
    () =>
      data.engagementByContentPurpose.map((point) => ({
        x: t(`page.analytics-community-feed.engagement-content.${point.x as CommunityFeedAnalyticsContentPurpose}`),
        y: point.interactionsPerPost,
      })),
    [data.engagementByContentPurpose, t],
  );
  const audienceData = useMemo(
    () =>
      data.audienceScopes.map((scope, index) => ({
        label:
          scope.type === "projectOnly"
            ? t("page.analytics-community-feed.connections.project-only")
            : (scope.projectConnection?.name ?? t("page.analytics-community-feed.connections.project-only")),
        color: ANALYTICS_AUDIENCE_SCOPE_COLORS[index % ANALYTICS_AUDIENCE_SCOPE_COLORS.length],
        dataPoints: scope.dataPoints.map((point) => ({
          x: point.x,
          y: point.residentPosts + point.projectTeamPosts,
          isPending: point.isCurrentMonth,
        })),
      })),
    [data.audienceScopes, t],
  );
  const responseTimeData = useMemo(
    () =>
      data.responseTimeDistribution.map((point) => ({
        category: point.x as CommunityFeedAnalyticsResponseBucket,
        label: t(`page.analytics-community-feed.response-bucket.${point.x as CommunityFeedAnalyticsResponseBucket}`),
        value: point.y,
      })),
    [data.responseTimeDistribution, t],
  );
  const answeredHelpData = useMemo(
    () =>
      data.monthly.map((point) => ({
        x: point.x,
        y: point.answeredHelpRequests,
        isPending: point.isCurrentMonth,
      })),
    [data.monthly],
  );
  const unansweredHelpData = useMemo(
    () =>
      data.monthly.map((point) => ({
        x: point.x,
        y: point.unansweredHelpRequests,
        isPending: point.isCurrentMonth,
      })),
    [data.monthly],
  );
  const showMessageAnalytics = mode === "focused" || contentType !== "poll";
  const showPollAnalytics = contentType === "all" || contentType === "poll";
  const residentPostPercentage =
    summary.visiblePosts === 0 ? 0 : Math.round((summary.residentPosts / summary.visiblePosts) * 100);
  const answeredHelpRequests = summary.helpRequests - summary.unansweredHelpRequests;

  return (
    <PageGrid.Row>
      {showMessageAnalytics && (
        <>
          <PageGrid.Column size="100%">
            <AnalyticsSection
              isFullWidth
              title={
                mode === "focused"
                  ? t("page.analytics-community-feed.focus.signals")
                  : t("page.analytics-community-feed.overview.section-title")
              }
            >
              <AnalyticsCard.Root>
                <AnalyticsCard.Header
                  title={t("page.analytics-community-feed.overview.title")}
                  tooltip={help("page.analytics-community-feed.overview.title.tooltip")}
                />
                <section className="flex flex-col gap-space-md @xs:flex-row @xs:flex-wrap">
                  <Stat
                    label={t("page.analytics-community-feed.overview.visible-posts")}
                    value={summary.visiblePosts}
                    renderFooter={() =>
                      t("page.analytics-community-feed.overview.previous-period-change", {
                        change: formatSignedChange(
                          summary.visiblePosts - summary.previousPeriod.visiblePosts,
                          i18n.language,
                        ),
                      })
                    }
                  />
                  <Stat
                    label={t("page.analytics-community-feed.overview.resident-posts")}
                    value={summary.residentPosts}
                    renderFooter={() =>
                      t("page.analytics-community-feed.overview.resident-share", {
                        percentage: residentPostPercentage,
                      })
                    }
                  />
                  <Stat
                    label={t("page.analytics-community-feed.overview.contributors")}
                    value={summary.uniqueResidentContributors}
                    renderFooter={() =>
                      t("page.analytics-community-feed.overview.contributor-reach", {
                        percentage: summary.contributorReachPercentage,
                        eligible: summary.eligibleResidents,
                      })
                    }
                  />
                  <Stat
                    label={t("page.analytics-community-feed.overview.average-interactions")}
                    value={summary.averageInteractionsPerPost}
                    renderValue={(value) => value?.toFixed(1) ?? "-"}
                    renderFooter={() =>
                      t("page.analytics-community-feed.overview.previous-period-change", {
                        change: formatSignedChange(
                          summary.averageInteractionsPerPost - summary.previousPeriod.averageInteractionsPerPost,
                          i18n.language,
                        ),
                      })
                    }
                  />
                  <Stat
                    label={t("page.analytics-community-feed.overview.help-response-rate")}
                    value={summary.helpResponseRatePercentage}
                    renderValue={(value) => (value === undefined ? "-" : `${value}%`)}
                    renderFooter={() =>
                      t("page.analytics-community-feed.overview.help-response-detail", {
                        answered: answeredHelpRequests,
                        total: summary.helpRequests,
                        previous: summary.previousPeriod.helpResponseRatePercentage,
                      })
                    }
                  />
                  <Stat
                    label={t("page.analytics-community-feed.overview.median-response")}
                    value={summary.medianFirstResponseHours}
                    renderValue={(value) =>
                      value === undefined ? "-" : t("page.analytics-community-feed.value.hours", { count: value })
                    }
                    renderFooter={() =>
                      t("page.analytics-community-feed.overview.previous-response", {
                        value:
                          summary.previousPeriod.medianFirstResponseHours === undefined
                            ? "-"
                            : t("page.analytics-community-feed.value.hours", {
                                count: summary.previousPeriod.medianFirstResponseHours,
                              }),
                      })
                    }
                  />
                </section>
              </AnalyticsCard.Root>
            </AnalyticsSection>
          </PageGrid.Column>

          {mode === "all" && isSentimentAvailable && data.sentiment.hasSufficientData && (
            <SentimentSummaryCard sentiment={data.sentiment} dateRange={selectedDateRange.value} />
          )}

          {mode === "all" && (
            <PageGrid.Column size="100%">
              <AnalyticsSection
                title={t("page.analytics-community-feed.ownership.section-title")}
                description={t("page.analytics-community-feed.ownership.description")}
              >
                <AnalyticsCard.Root variant="graph" height="content">
                  <AnalyticsCard.Header
                    title={t("page.analytics-community-feed.ownership.posts-per-month")}
                    tooltip={help("page.analytics-community-feed.ownership.posts-per-month.tooltip")}
                  />
                  <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
                    <BarChart
                      dataPrimary={{
                        data: residentPostsData,
                      }}
                      dataSecondary={{
                        data: projectTeamPostsData,
                        color: dataColors.green,
                      }}
                      xAxisType="date"
                      yLabel={t("page.analytics-community-feed.axis.posts")}
                      yTickMode="integer"
                      hoverLabel={(bar) => (
                        <Stack gap="xs">
                          <span>
                            {t("page.analytics-community-feed.ownership.resident-created")}: {bar.primary?.y ?? 0}
                          </span>
                          <span>
                            {t("page.analytics-community-feed.ownership.project-team-created")}: {bar.secondary?.y ?? 0}
                          </span>
                          {(bar.primary?.isPending || bar.secondary?.isPending) && (
                            <span>
                              {t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}
                            </span>
                          )}
                        </Stack>
                      )}
                    />
                    <ChartLegend
                      items={[
                        {
                          label: t("page.analytics-community-feed.ownership.resident-created"),
                          color: dataColors.primary,
                        },
                        {
                          label: t("page.analytics-community-feed.ownership.project-team-created"),
                          color: dataColors.green,
                        },
                        {
                          label: t("page.analytics-details.engagement.chart.legend.current-period"),
                          color: dataColors.primary,
                          pattern: "striped",
                        },
                      ]}
                    />
                  </figure>
                </AnalyticsCard.Root>
                <AnalyticsCard.Root variant="graph" height="content">
                  <AnalyticsCard.Header
                    title={t("page.analytics-community-feed.participation.contributors-per-month")}
                    tooltip={help("page.analytics-community-feed.participation.contributors-per-month.tooltip")}
                  />
                  <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
                    <LineChart
                      data={contributorData}
                      xAxisType="date"
                      yLabel={t("page.analytics-community-feed.axis.residents")}
                      hoverLabel={(point) => (
                        <Stack gap="xs">
                          <span>{point.relatedPoints[0].dataPoint.y}</span>
                          {point.relatedPoints[0].dataPoint.isPending && (
                            <span>
                              {t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}
                            </span>
                          )}
                        </Stack>
                      )}
                    />
                    <ChartLegend items={[{ label: contributorData[0].label, color: dataColors.green }]} />
                  </figure>
                </AnalyticsCard.Root>
                <AnalyticsCard.Root variant="graph" height="content">
                  <AnalyticsCard.Header
                    title={t("page.analytics-community-feed.ownership.contributor-reach-title")}
                    tooltip={help("page.analytics-community-feed.ownership.contributor-reach-title.tooltip")}
                  />
                  <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
                    <LineChart
                      data={contributorReachData}
                      xAxisType="date"
                      yLabel={t("page.analytics-community-feed.axis.resident-reach")}
                      formatYTick={(value) => `${value}%`}
                      hoverLabel={(point) => (
                        <Stack gap="xs">
                          <span>{point.relatedPoints[0].dataPoint.y}%</span>
                          {point.relatedPoints[0].dataPoint.isPending && (
                            <span>
                              {t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}
                            </span>
                          )}
                        </Stack>
                      )}
                    />
                    <ChartLegend items={[{ label: contributorReachData[0].label, color: dataColors.mint }]} />
                  </figure>
                </AnalyticsCard.Root>
                <AnalyticsCard.Root height="content">
                  <AnalyticsCard.Header
                    title={t("page.analytics-community-feed.ownership.frequency-title")}
                    tooltip={help("page.analytics-community-feed.ownership.frequency-title.tooltip")}
                  />
                  <HistogramChart data={contributorFrequencyData} />
                </AnalyticsCard.Root>
              </AnalyticsSection>
            </PageGrid.Column>
          )}

          {mode === "all" && (
            <PageGrid.Column size="100%">
              <AnalyticsSection
                title={t("page.analytics-community-feed.content.section-title")}
                description={t("page.analytics-community-feed.content.description")}
              >
                <AnalyticsCard.Root variant="graph" height="content">
                  <AnalyticsCard.Header
                    title={t("page.analytics-community-feed.content.purpose-title")}
                    tooltip={help("page.analytics-community-feed.content.purpose-title.tooltip")}
                  />
                  <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
                    <LineChart
                      data={contentPurposeData}
                      xAxisType="date"
                      yLabel={t("page.analytics-community-feed.axis.posts")}
                      hoverLabel={(point) => (
                        <ul className="flex flex-col gap-space-xs">
                          {point.relatedPoints.map((relatedPoint) => (
                            <li key={relatedPoint.label}>
                              {relatedPoint.label}: {relatedPoint.dataPoint.y}
                            </li>
                          ))}
                          {point.relatedPoints.some((relatedPoint) => relatedPoint.dataPoint.isPending) && (
                            <li>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</li>
                          )}
                        </ul>
                      )}
                    />
                    <ChartLegend
                      items={contentPurposeData.map((series) => ({ label: series.label, color: series.color }))}
                    />
                  </figure>
                </AnalyticsCard.Root>
                <AnalyticsCard.Root height="content">
                  <AnalyticsCard.Header
                    title={t("page.analytics-community-feed.content.audience-types-title")}
                    tooltip={help("page.analytics-community-feed.content.audience-types-title.tooltip")}
                  />
                  <HistogramChart data={audienceTypeData} />
                </AnalyticsCard.Root>
              </AnalyticsSection>
            </PageGrid.Column>
          )}

          {mode === "all" && (
            <PageGrid.Column size="100%">
              <AnalyticsSection
                isFullWidth
                title={t("page.analytics-community-feed.participation.section-title")}
                description={t("page.analytics-community-feed.participation.description")}
              >
                <AnalyticsCard.Root variant="graph" height="content">
                  <AnalyticsCard.Header
                    title={t("page.analytics-community-feed.participation.comments-reactions")}
                    tooltip={help("page.analytics-community-feed.participation.comments-reactions.tooltip")}
                  />
                  <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
                    <LineChart
                      data={interactionData}
                      xAxisType="date"
                      yLabel={t("page.analytics-community-feed.axis.interactions")}
                      hoverLabel={(point) => (
                        <ul className="flex flex-col gap-space-xs">
                          {point.relatedPoints.map((relatedPoint) => (
                            <li key={relatedPoint.label}>
                              {relatedPoint.label}: {relatedPoint.dataPoint.y}
                            </li>
                          ))}
                          {point.relatedPoints.some((relatedPoint) => relatedPoint.dataPoint.isPending) && (
                            <li>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</li>
                          )}
                        </ul>
                      )}
                    />
                    <ChartLegend
                      items={interactionData.map((series) => ({ label: series.label, color: series.color }))}
                    />
                  </figure>
                </AnalyticsCard.Root>
                <AnalyticsCard.Root variant="graph" height="content">
                  <AnalyticsCard.Header
                    title={t("page.analytics-community-feed.participation.interactions-per-post-title")}
                    tooltip={help("page.analytics-community-feed.participation.interactions-per-post-title.tooltip")}
                  />
                  <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
                    <LineChart
                      data={interactionsPerPostData}
                      xAxisType="date"
                      yLabel={t("page.analytics-community-feed.axis.interactions-per-post")}
                      hoverLabel={(point) => (
                        <Stack gap="xs">
                          <span>{point.relatedPoints[0].dataPoint.y}</span>
                          {point.relatedPoints[0].dataPoint.isPending && (
                            <span>
                              {t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}
                            </span>
                          )}
                        </Stack>
                      )}
                    />
                    <ChartLegend items={[{ label: interactionsPerPostData[0].label, color: dataColors.purple }]} />
                  </figure>
                </AnalyticsCard.Root>
                <AnalyticsCard.Root height="content">
                  <AnalyticsCard.Header
                    title={t("page.analytics-community-feed.participation.interaction-level-title")}
                    tooltip={help("page.analytics-community-feed.participation.interaction-level-title.tooltip")}
                  />
                  <HistogramChart data={interactionDistributionData} />
                </AnalyticsCard.Root>
                <AnalyticsCard.Root variant="graph" height="content">
                  <AnalyticsCard.Header
                    title={t("page.analytics-community-feed.participation.content-engagement-title")}
                    tooltip={help("page.analytics-community-feed.participation.content-engagement-title.tooltip")}
                  />
                  <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
                    <BarChart
                      dataPrimary={{ data: engagementByContentPurposeData, color: dataColors.orange }}
                      yLabel={t("page.analytics-community-feed.axis.interactions-per-post")}
                    />
                    <ChartLegend
                      items={[
                        {
                          label: t("page.analytics-community-feed.participation.average-interactions"),
                          color: dataColors.orange,
                        },
                      ]}
                    />
                  </figure>
                </AnalyticsCard.Root>
              </AnalyticsSection>
            </PageGrid.Column>
          )}

          {mode === "all" && data.hasProjectConnections && (
            <PageGrid.Column size="100%">
              <AnalyticsSection
                isFullWidth
                title={t("page.analytics-community-feed.connections.section-title")}
                description={t("page.analytics-community-feed.connections.description")}
              >
                <AnalyticsCard.Root variant="graph" height="content">
                  <AnalyticsCard.Header
                    title={t("page.analytics-community-feed.connections.chart-title")}
                    tooltip={help("page.analytics-community-feed.connections.chart-title.tooltip")}
                  />
                  <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
                    <LineChart
                      data={audienceData}
                      xAxisType="date"
                      yLabel={t("page.analytics-community-feed.axis.posts")}
                      hoverLabel={(point) => (
                        <ul className="flex flex-col gap-space-xs">
                          {point.relatedPoints.map((relatedPoint) => (
                            <li key={relatedPoint.label}>
                              {relatedPoint.label}: {relatedPoint.dataPoint.y}
                            </li>
                          ))}
                          {point.relatedPoints.some((relatedPoint) => relatedPoint.dataPoint.isPending) && (
                            <li>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</li>
                          )}
                        </ul>
                      )}
                    />
                    <ChartLegend items={audienceData.map((series) => ({ label: series.label, color: series.color }))} />
                  </figure>
                </AnalyticsCard.Root>
              </AnalyticsSection>
            </PageGrid.Column>
          )}

          <PageGrid.Column size="100%">
            <AnalyticsSection
              isFullWidth
              title={t("page.analytics-community-feed.help.section-title")}
              description={t("page.analytics-community-feed.help.description")}
            >
              <AnalyticsCard.Root height="content">
                <AnalyticsCard.Header
                  title={t("page.analytics-community-feed.help.response-time")}
                  tooltip={help("page.analytics-community-feed.help.response-time.tooltip")}
                />
                <HistogramChart data={responseTimeData} />
              </AnalyticsCard.Root>
              <AnalyticsCard.Root variant="graph" height="content">
                <AnalyticsCard.Header
                  title={t("page.analytics-community-feed.help.answered-per-month")}
                  tooltip={help("page.analytics-community-feed.help.answered-per-month.tooltip")}
                />
                <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
                  <BarChart
                    dataPrimary={{ data: answeredHelpData, color: dataColors.green }}
                    dataSecondary={{ data: unansweredHelpData, color: dataColors.orange }}
                    xAxisType="date"
                    yLabel={t("page.analytics-community-feed.axis.help-requests")}
                    yTickMode="integer"
                  />
                  <ChartLegend
                    items={[
                      {
                        label: t("page.analytics-community-feed.help.answered"),
                        color: dataColors.green,
                      },
                      {
                        label: t("page.analytics-community-feed.help.unanswered"),
                        color: dataColors.orange,
                      },
                    ]}
                  />
                </figure>
              </AnalyticsCard.Root>
            </AnalyticsSection>
          </PageGrid.Column>

          {showAudienceParticipation && (
            <PageGrid.Column size="100%">
              <AnalyticsSection
                isFullWidth
                title={t("page.analytics-community-feed.groups.section-title")}
                description={t("page.analytics-community-feed.groups.description")}
              >
                <AnalyticsCard.Root horizontalScroll>
                  <AnalyticsCard.Header title={t("page.analytics-community-feed.groups.title")} />
                  <figure className="invisible hidden @sm:visible @sm:block">
                    <Table table={groupTable} data-testid="analytics-community-feed-groups-table" />
                  </figure>
                  <MobileTableCards table={groupTable} />
                </AnalyticsCard.Root>
              </AnalyticsSection>
            </PageGrid.Column>
          )}
        </>
      )}

      {mode === "all" && showPollAnalytics && (
        <PollAnalyticsSection polls={data.polls} hasProjectConnections={data.hasProjectConnections} />
      )}
    </PageGrid.Row>
  );
}

function MobileTableCards<TData extends RowData>({ table }: { table: DashboardTable<TData> }): React.ReactNode {
  return (
    <section
      className="flex flex-col gap-space-md @sm:hidden"
      data-testid="analytics-community-feed-groups-mobile-list"
    >
      {table.getRowModel().rows.map((row) => (
        <SectionCard key={row.id}>
          <dl className="flex flex-col gap-space-md">
            {row.getVisibleCells().map((cell) => {
              const header = cell.column.columnDef.header;

              return (
                <div key={cell.id} className="flex items-start justify-between gap-space-md">
                  <dt className="text-caption text-text-secondary">
                    {typeof header === "string" ? header : cell.column.id}
                  </dt>
                  <dd className="min-w-0 text-right text-caption-emphasis">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </dd>
                </div>
              );
            })}
          </dl>
        </SectionCard>
      ))}
    </section>
  );
}
